import React, { useEffect, useRef, useState } from 'react';
import { Board, Tile, Position, GameStatus } from '@snap-pairs/game-core';

interface GameCanvasProps {
  board: Board;
  selectedTile: Tile | null;
  validMatches: Tile[];
  hintTiles: Tile[];
  onTileClick: (tile: Tile) => void;
  canvasSize: number;
  gameStatus: GameStatus;
  darkMode: boolean;
  isShuffling: boolean;
}

interface TileRect {
  x: number;
  y: number;
  width: number;
  height: number;
  tile: Tile;
}

const GameCanvas: React.FC<GameCanvasProps> = ({
  board,
  selectedTile,
  validMatches,
  hintTiles,
  onTileClick,
  canvasSize,
  gameStatus,
  darkMode,
  isShuffling
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tileRectsRef = useRef<TileRect[]>([]);
  const shuffleOffsetsRef = useRef<Map<string, { x: number; y: number; rotation: number }>>(new Map());
  const shuffleStartTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number>(0);

  const getSuitColor = (symbol: string): string => {
    // Get Unicode code point of the symbol
    const codePoint = symbol.codePointAt(0);
    if (!codePoint) return '#808080'; // Gray fallback

    // Joker tile (🀟 - U+1F01F) - Special purple/gold color
    if (codePoint === 0x1F01F) {
      return '#9C27B0'; // Purple for wildcard
    }

    // Traditional Mahjong suit colors
    // 🀀-🀆 (U+1F000 to U+1F006): Winds and Dragons
    if (codePoint >= 0x1F000 && codePoint <= 0x1F006) {
      if (codePoint === 0x1F004) return '#D32F2F'; // Red Dragon - Red
      if (codePoint === 0x1F005) return '#388E3C'; // Green Dragon - Green
      if (codePoint === 0x1F006) return '#E0E0E0'; // White Dragon - Light gray
      return '#FFA726'; // Winds - Orange/gold
    }

    // 🀇-🀏 (U+1F007 to U+1F00F): Characters/Wan - Red
    if (codePoint >= 0x1F007 && codePoint <= 0x1F00F) {
      return '#D32F2F'; // Red
    }

    // 🀐-🀘 (U+1F010 to U+1F018): Bamboo/Suo - Green
    if (codePoint >= 0x1F010 && codePoint <= 0x1F018) {
      return '#388E3C'; // Green
    }

    // 🀙-🀡 (U+1F019 to U+1F021): Dots/Bing - Blue
    if (codePoint >= 0x1F019 && codePoint <= 0x1F021) {
      return '#1976D2'; // Blue
    }

    // Default fallback
    return '#808080'; // Gray
  };

  const getColorForSymbol = (symbol: string, isDark: boolean): string => {
    // In dark mode, use dark background for tiles
    if (isDark) {
      return '#1a1a1a';
    }
    // In light mode, use the suit color for tile background
    return getSuitColor(symbol);
  };

  // Handle shuffle animation
  useEffect(() => {
    if (isShuffling) {
      // Generate random offsets for each tile
      shuffleOffsetsRef.current.clear();
      board.tiles.forEach(tile => {
        if (!tile.matched) {
          shuffleOffsetsRef.current.set(tile.id, {
            x: (Math.random() - 0.5) * 60,
            y: (Math.random() - 0.5) * 60,
            rotation: (Math.random() - 0.5) * 25
          });
        }
      });
      shuffleStartTimeRef.current = Date.now();

      const animate = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        drawBoard(ctx, board, selectedTile, validMatches, hintTiles, canvasSize, gameStatus, darkMode);

        if (isShuffling) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      shuffleOffsetsRef.current.clear();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isShuffling, board, selectedTile, validMatches, hintTiles, canvasSize, gameStatus, darkMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    drawBoard(ctx, board, selectedTile, validMatches, hintTiles, canvasSize, gameStatus, darkMode);
  }, [board, selectedTile, validMatches, hintTiles, canvasSize, gameStatus, darkMode]);

  const drawBoard = (
    ctx: CanvasRenderingContext2D,
    board: Board,
    selectedTile: Tile | null,
    validMatches: Tile[],
    hintTiles: Tile[],
    size: number,
    status: GameStatus,
    isDarkMode: boolean
  ) => {
    const { gridSize, tiles } = board;
    const padding = 10;
    const tileSpacing = 8;
    const availableSize = size - padding * 2;
    const tileSize = (availableSize - tileSpacing * (gridSize - 1)) / gridSize;

    tileRectsRef.current = [];

    ctx.clearRect(0, 0, size, size);

    const bgGradient = ctx.createLinearGradient(0, 0, size, size);
    bgGradient.addColorStop(0, '#0F2027');
    bgGradient.addColorStop(0.5, '#203A43');
    bgGradient.addColorStop(1, '#2C5364');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, size, size);

    // Only render tiles when game is playing
    if (status !== GameStatus.Playing) {
      return;
    }

    const validMatchIds = new Set(validMatches.map(t => t.id));
    const hintIds = new Set(hintTiles.map(t => t.id));
    const selectedId = selectedTile?.id;

    tiles.forEach(tile => {
      if (tile.matched) return;

      let x = padding + tile.position.col * (tileSize + tileSpacing);
      let y = padding + tile.position.row * (tileSize + tileSpacing);

      // Apply shuffle animation
      let rotation = 0;
      const shuffleOffset = shuffleOffsetsRef.current.get(tile.id);
      if (shuffleOffset) {
        const elapsed = Date.now() - shuffleStartTimeRef.current;
        const duration = 1000; // 1 second
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-in-out quartic for smoother motion)
        const eased = progress < 0.5
          ? 8 * progress * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 4) / 2;

        // Apply animated offset
        x += shuffleOffset.x * (1 - eased);
        y += shuffleOffset.y * (1 - eased);
        rotation = shuffleOffset.rotation * (1 - eased);
      }

      tileRectsRef.current.push({
        x,
        y,
        width: tileSize,
        height: tileSize,
        tile
      });

      const isSelected = tile.id === selectedId;
      const isValidMatch = validMatchIds.has(tile.id);
      const isHint = hintIds.has(tile.id);
      const baseColor = getColorForSymbol(tile.symbol, isDarkMode);

      ctx.save();

      // Apply rotation if shuffling
      if (rotation !== 0) {
        const centerX = x + tileSize / 2;
        const centerY = y + tileSize / 2;
        ctx.translate(centerX, centerY);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-centerX, -centerY);
      }

      if (isSelected) {
        ctx.fillStyle = baseColor;
        ctx.shadowBlur = 20;
        ctx.shadowColor = baseColor;
      } else if (isValidMatch) {
        const gradient = ctx.createLinearGradient(x, y, x + tileSize, y + tileSize);
        gradient.addColorStop(0, baseColor);
        gradient.addColorStop(1, '#FFD700');
        ctx.fillStyle = gradient;
        ctx.shadowBlur = 15;
        ctx.shadowColor = '#FFD700';
      } else if (isHint) {
        ctx.fillStyle = baseColor;
        ctx.shadowBlur = 25;
        ctx.shadowColor = '#FF6B00';
      } else {
        ctx.fillStyle = baseColor;
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
      }

      const radius = 8;
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + tileSize - radius, y);
      ctx.quadraticCurveTo(x + tileSize, y, x + tileSize, y + radius);
      ctx.lineTo(x + tileSize, y + tileSize - radius);
      ctx.quadraticCurveTo(x + tileSize, y + tileSize, x + tileSize - radius, y + tileSize);
      ctx.lineTo(x + radius, y + tileSize);
      ctx.quadraticCurveTo(x, y + tileSize, x, y + tileSize - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.fill();

      ctx.shadowBlur = 0;
      // In dark mode, use suit color for the symbol; in light mode, use white
      ctx.fillStyle = isDarkMode ? getSuitColor(tile.symbol) : '#ffffff';
      ctx.font = `${tileSize * 0.5}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tile.symbol, x + tileSize / 2, y + tileSize / 2);

      ctx.restore();
    });
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    const clickedTileRect = tileRectsRef.current.find(
      tr =>
        x >= tr.x &&
        x <= tr.x + tr.width &&
        y >= tr.y &&
        y <= tr.y + tr.height
    );

    if (clickedTileRect) {
      onTileClick(clickedTileRect.tile);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={canvasSize}
      height={canvasSize}
      onClick={handleCanvasClick}
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        cursor: 'pointer',
        touchAction: 'manipulation'
      }}
    />
  );
};

export default GameCanvas;
